"use client";

import React, { useState } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createGoalAction,
  updateGoalAction,
  deleteGoalAction,
} from "@/actions/goals-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Target,
  Calendar,
  User,
  Plus,
  Search,
  Sparkles,
  TrendingUp,
  TrendingDown,
  Minus,
  CheckCircle2,
  AlertTriangle,
  Edit2,
  Trash2,
} from "lucide-react";

interface GoalItem {
  id: string;
  title: string;
  progress: number;
  trend: "stable" | "up" | "down";
  targetDate: string;
  status: "healthy" | "attention" | "at_risk" | "critical";
  confidenceForecast: number;
  ownerName: string | null;
  ownerId: string | null;
}

interface AgentItem {
  id: string;
  name: string;
}

interface GoalsClientProps {
  initialGoals: GoalItem[];
  agents: AgentItem[];
}

export function GoalsClient({ initialGoals, agents }: GoalsClientProps) {
  const router = useRouter();
  const [goalsList, setGoalsList] = useState<GoalItem[]>(initialGoals);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingGoal, setEditingGoal] = useState<GoalItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Form State
  const [newTitle, setNewTitle] = useState("");
  const [newTargetDate, setNewTargetDate] = useState("");
  const [newStatus, setNewStatus] = useState<
    "healthy" | "attention" | "at_risk" | "critical"
  >("healthy");
  const [newConfidence, setNewConfidence] = useState("100");
  const [newOwnerId, setNewOwnerId] = useState("");

  // Sync state with server props
  React.useEffect(() => {
    setGoalsList(initialGoals);
  }, [initialGoals]);

  const handleOpenModal = (goal?: GoalItem) => {
    setError("");
    setLoading(false);
    if (goal) {
      setEditingGoal(goal);
      setNewTitle(goal.title);
      setNewTargetDate(goal.targetDate.split("T")[0]);
      setNewStatus(goal.status);
      setNewConfidence(goal.confidenceForecast.toString());
      setNewOwnerId(goal.ownerId || "");
    } else {
      setEditingGoal(null);
      setNewTitle("");
      const nextQuarter = new Date();
      nextQuarter.setDate(nextQuarter.getDate() + 90);
      setNewTargetDate(nextQuarter.toISOString().split("T")[0]);
      setNewStatus("healthy");
      setNewConfidence("100");
      setNewOwnerId("");
    }
    setIsModalOpen(true);
  };

  const handleCreateGoal = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!newTitle.trim()) {
      setError("Il titolo dell'obiettivo è obbligatorio.");
      return;
    }
    if (!newTargetDate) {
      setError("La data di scadenza è obbligatoria.");
      return;
    }
    const confidence = parseInt(newConfidence);
    if (isNaN(confidence) || confidence < 0 || confidence > 100) {
      setError(
        "La confidenza stimata deve essere un numero compreso tra 0 e 100.",
      );
      return;
    }

    setLoading(true);
    try {
      let res;
      if (editingGoal) {
        res = await updateGoalAction({
          id: editingGoal.id,
          title: newTitle,
          targetDate: newTargetDate,
          status: newStatus,
          confidenceForecast: confidence,
          ownerId: newOwnerId || null,
        });
      } else {
        res = await createGoalAction({
          title: newTitle,
          targetDate: newTargetDate,
          status: newStatus,
          confidenceForecast: confidence,
          ownerId: newOwnerId || null,
        });
      }

      if (res.error) {
        setError(res.error);
      } else {
        setIsModalOpen(false);
        router.refresh();
      }
    } catch (err: any) {
      setError(
        err.message || "Si è verificato un errore durante il salvataggio.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGoal = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questo obiettivo?")) return;
    try {
      const res = await deleteGoalAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err: any) {
      alert(err.message || "Errore durante l'eliminazione.");
    }
  };

  const filteredGoals = goalsList.filter((goal) => {
    const matchesSearch = goal.title
      .toLowerCase()
      .includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || goal.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: GoalItem["status"]) => {
    switch (status) {
      case "healthy":
        return <Badge variant="success">Sano</Badge>;
      case "attention":
        return <Badge variant="warning">Attenzione</Badge>;
      case "at_risk":
        return <Badge variant="error">A Rischio</Badge>;
      case "critical":
        return <Badge variant="error">Critico</Badge>;
    }
  };

  const getTrendIcon = (trend: GoalItem["trend"]) => {
    switch (trend) {
      case "up":
        return <TrendingUp className="h-4 w-4 text-success" />;
      case "down":
        return <TrendingDown className="h-4 w-4 text-error" />;
      case "stable":
      default:
        return <Minus className="h-4 w-4 text-text-muted" />;
    }
  };

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    if (isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("it-IT", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  return (
    <div className="space-y-6">
      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca obiettivi..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex w-full sm:w-auto gap-3 items-center justify-between sm:justify-end">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-[150px]"
          >
            <option value="all">Tutti gli stati</option>
            <option value="healthy">Sano (Healthy)</option>
            <option value="attention">Attenzione</option>
            <option value="at_risk">A Rischio</option>
            <option value="critical">Critico</option>
          </Select>

          <Button
            onClick={() => handleOpenModal()}
            className="gap-1.5 shrink-0"
          >
            <Plus className="h-4 w-4" />
            Nuovo Obiettivo
          </Button>
        </div>
      </div>

      {/* Goals Grid */}
      {filteredGoals.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center max-w-xl mx-auto my-8">
          <Target className="h-10 w-10 text-primary-500 mb-3 animate-pulse" />
          <h3 className="text-base font-bold text-text-primary">
            Nessun obiettivo trovato
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Non ci sono obiettivi strategici corrispondenti ai filtri
            selezionati. Crea un nuovo obiettivo per cominciare.
          </p>
          <Button onClick={() => handleOpenModal()} className="mt-4 gap-1.5">
            <Plus className="h-4 w-4" />
            Crea Primo Obiettivo
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredGoals.map((goal) => (
            <Card
              key={goal.id}
              onClick={() => handleOpenModal(goal)}
              className="group relative overflow-hidden flex flex-col justify-between hover:border-primary-500/50 transition-all border-l-4 border-l-primary-500 cursor-pointer"
            >
              <CardHeader className="pb-3">
                <div className="flex justify-between items-start gap-4">
                  <div className="space-y-1">
                    <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                      Obiettivo Strategico
                    </span>
                    <Link
                      href={`/strategy/goals/${goal.id}`}
                      onClick={(e) => e.stopPropagation()}
                      className="block"
                    >
                      <CardTitle className="hover:text-primary-600 transition-colors text-base font-bold leading-snug">
                        {goal.title}
                      </CardTitle>
                    </Link>
                  </div>
                  <div
                    className="relative flex items-center gap-1.5 shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {/* Badges container */}
                    <div className="flex items-center gap-1.5 transition-opacity group-hover:opacity-0 group-hover:pointer-events-none">
                      {getTrendIcon(goal.trend)}
                      {getStatusBadge(goal.status)}
                    </div>
                    {/* Hover actions container */}
                    <div className="absolute right-0 top-1/2 -translate-y-1/2 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none group-hover:pointer-events-auto">
                      <Link
                        href={`/explore?nodeId=${goal.id}`}
                        className="p-1 hover:bg-card-muted text-text-secondary hover:text-primary-600 rounded transition-colors"
                        title="Grafo"
                      >
                        <TrendingUp className="h-3.5 w-3.5" />
                      </Link>
                      <button
                        onClick={() => handleOpenModal(goal)}
                        className="p-1 hover:bg-card-muted text-text-secondary hover:text-foreground rounded transition-colors"
                        title="Modifica"
                      >
                        <Edit2 className="h-3.5 w-3.5" />
                      </button>
                      <button
                        onClick={() => handleDeleteGoal(goal.id)}
                        className="p-1 hover:bg-card-muted text-error hover:text-error/80 rounded transition-colors"
                        title="Elimina"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4 pb-4">
                {/* Progress bar */}
                <div className="space-y-1.5">
                  <div className="flex justify-between text-xs font-semibold">
                    <span className="text-text-secondary">Progressi OKR</span>
                    <span className="text-text-primary">{goal.progress}%</span>
                  </div>
                  <div className="h-2 w-full bg-border-strong/20 rounded-full overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-primary-500 to-primary-600 h-full rounded-full transition-all duration-500"
                      style={{ width: `${goal.progress}%` }}
                    />
                  </div>
                </div>

                {/* Date & Owner */}
                <div className="grid grid-cols-2 gap-2 text-xs border-t border-border/40 pt-3">
                  <div className="flex items-center gap-1.5 text-text-secondary">
                    <Calendar className="h-3.5 w-3.5" />
                    <span>
                      Scadenza: <strong>{formatDate(goal.targetDate)}</strong>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5 text-text-secondary">
                    <User className="h-3.5 w-3.5" />
                    {goal.ownerId ? (
                      <Link
                        href={`/explore?nodeId=${goal.ownerId}`}
                        onClick={(e) => e.stopPropagation()}
                        className="truncate hover:text-primary-600 hover:underline transition-colors"
                      >
                        Owner: <strong>{goal.ownerName}</strong>
                      </Link>
                    ) : (
                      <span className="truncate">
                        Owner: <strong>Nessuno</strong>
                      </span>
                    )}
                  </div>
                </div>
              </CardContent>
              <CardFooter
                onClick={(e) => e.stopPropagation()}
                className="flex justify-between items-center bg-card-muted/30 px-6 py-3 border-t border-border/20 text-xs gap-3"
              >
                <span className="text-text-muted">
                  Confidenza:{" "}
                  <strong className="text-text-primary">
                    {goal.confidenceForecast}%
                  </strong>
                </span>
                <div className="flex gap-2.5">
                  <Link
                    href={`/explore?nodeId=${goal.id}`}
                    className="text-text-secondary hover:text-primary-600 font-semibold flex items-center gap-1"
                  >
                    <Target className="h-3.5 w-3.5 text-text-muted" /> Grafo
                  </Link>
                  <Link
                    href={`/strategy/goals/${goal.id}`}
                    className="text-primary-600 hover:text-primary-700 font-semibold flex items-center gap-1"
                  >
                    Vedi Key Results &rarr;
                  </Link>
                </div>
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {/* Add Goal Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingGoal ? "Modifica Obiettivo" : "Aggiungi Nuovo Obiettivo"}
            </DialogTitle>
            <DialogDescription>
              Definisci o aggiorna un obiettivo strategico ad alto livello.
              Potrai successivamente definire i Key Results.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleCreateGoal} className="space-y-4 my-2">
            <div className="space-y-1.5">
              <Label htmlFor="goal-title">Titolo Obiettivo</Label>
              <Input
                id="goal-title"
                placeholder="es. Ottimizzare le conversioni Checkout del 25%"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                disabled={loading}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="goal-date">Scadenza Target</Label>
                <Input
                  id="goal-date"
                  type="date"
                  value={newTargetDate}
                  onChange={(e) => setNewTargetDate(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="goal-status">Stato Iniziale</Label>
                <Select
                  id="goal-status"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="healthy">Sano (Healthy)</option>
                  <option value="attention">Attenzione</option>
                  <option value="at_risk">A Rischio</option>
                  <option value="critical">Critico</option>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="goal-owner">Proprietario (Owner)</Label>
                <Select
                  id="goal-owner"
                  value={newOwnerId}
                  onChange={(e) => setNewOwnerId(e.target.value)}
                  disabled={loading}
                >
                  <option value="">Nessuno (Non assegnato)</option>
                  {agents.map((agent) => (
                    <option key={agent.id} value={agent.id}>
                      {agent.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="goal-confidence">Confidenza Stimata (%)</Label>
                <Input
                  id="goal-confidence"
                  type="number"
                  min="0"
                  max="100"
                  value={newConfidence}
                  onChange={(e) => setNewConfidence(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <DialogContent className="hidden" />{" "}
              {/* standard closing anchor */}
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {editingGoal ? "Salva Modifiche" : "Crea Obiettivo"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
